import { ChangeEvent, Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { Panel, PanelHeader, PageHeader } from '../components/ui/Panel';
import Pill from '../components/ui/Pill';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { LoadingPanel, ErrorNotice, EmptyState, InfoNotice } from '../components/ui/States';
import { fmt } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import Checklist, { ProgressBar } from '../components/Checklist';
import { errorMessage, openBlankTab, showPrintable } from '../lib/printable';
import type {
  Contact as ContactView,
  ContactListQuery,
  ContactPage,
  DataResponse,
  UnmatchedResolveRequest,
  WebinarProductionRequest,
  WebinarUploadRequest,
} from '../contracts';

interface Webinar {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string | null;
  hasTime: boolean;
  url: string | null;
  fromWebsite: boolean;
  webinarId: string | null;
  attendance: number;
  unmatchedAttendees: number;
  unmatchedOpen: number;
  pollResponses: number;
  surveyResponses: number;
  questions: number;
  missingAttendance: boolean;
  upcoming: boolean;
  summary: Summary;
  checklist: { total: number; finished: number; open: number; overdue: number } | null;
}

interface Summary {
  webinarId: string | null;
  liveAttendees: number;
  attendees: number;
  nonMembers: number;
  uniqueViewers: number | null;
  peakConcurrent: number | null;
  pollResponses: number;
  surveyResponses: number;
  questions: number;
  answered: number;
  questionsFromMembers: number;
  recordingViewers: number;
  polls?: { poll: string; question: string; total: number; answers: { value: string; n: number }[] }[];
}

interface Person {
  id: string;
  name: string;
  email: string | null;
  title: string | null;
  organization: string | null;
  archived: boolean;
  bio: string | null;
}

interface Production {
  speakers: Person[];
  moderator: Person | null;
  coordinator: string | null;
  abstract: string | null;
  updatedAt: string | null;
}

interface WebinarDetail extends Webinar {
  isWebinar: boolean;
  production: Production;
  questionList:
    | {
        position: number;
        question: string;
        askedAt: string | null;
        answered: boolean;
        answerMode: 'live' | 'text' | null;
        upvotes: number;
        member: { id: string; name: string } | null;
      }[]
    | null;
}

type Contact = Pick<ContactView, 'id' | 'name' | 'email' | 'kinds'>;

interface QaPreview {
  dryRun: boolean;
  webinarId: string;
  topic: string | null;
  questions: number;
  answered: number;
  fromMembers: number;
  notMembers: number;
  anonymous: number;
  created: number;
  updated: number;
  unchanged: number;
  warnings: string[];
}

interface AttendancePreview {
  dryRun: boolean;
  webinarId: string;
  topic: string | null;
  startedAt: string | null;
  parsed: number;
  matched: number;
  created: number;
  existing: number;
  unmatched: number;
  unmatchedEmails: string[];
  warnings: string[];
}

interface PollPreview {
  dryRun: boolean;
  webinarId: string;
  topic: string | null;
  date: string;
  polls: {
    name: string | null;
    responses: number;
    emails: number;
    members: number;
    created: number;
    unchanged: number;
  }[];
  responses: number;
  members: number;
  emails: number;
  created: number;
  unchanged: number;
  warnings: string[];
}

interface UnmatchedAttendee {
  email: string;
  name: string | null;
  sessions: { id: string; sessionId: string; sessionTopic: string | null; occurredAt: string }[];
}

type Kind = 'attendance' | 'polls' | 'qa';
type Filter = 'past' | 'upcoming' | 'all';

const TZ = 'America/Toronto';
const SPREADSHEET = /\.(xlsx|xls|xlsm|numbers)$/i;

const day = (v: string) =>
  new Date(v).toLocaleDateString('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  });
const time = (v: string) =>
  new Date(v).toLocaleTimeString('en-CA', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false });

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="font-semibold tabular text-[22px] leading-none">
        {typeof value === 'number' ? fmt(value) : value}
      </div>
      <div className="text-small text-ink-2 mt-1">{label}</div>
    </div>
  );
}

function Warnings({ items }: { items: string[] }) {
  if (!items.length) return null;
  return (
    <ul className="mt-3 text-small space-y-1">
      {items.map((w) => (
        <li key={w} className="border-l-4 border-warning pl-2">
          {w}
        </li>
      ))}
    </ul>
  );
}

function UploadModal({
  webinar,
  kind,
  onClose,
  onDone,
}: {
  webinar: Webinar;
  kind: Kind;
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<{ name: string; content: string } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const path = `/webinars/${webinar.id}/${kind}`;

  const preview = useMutation(
    async (f: { name: string; content: string }) =>
      (
        await api.post(path, { fileName: f.name, content: f.content } satisfies WebinarUploadRequest, {
          timeout: 120000,
        })
      ).data.data as AttendancePreview & PollPreview & QaPreview,
  );
  const confirm = useMutation(
    async () =>
      (await api.post(path, { fileName: file?.name, content: file?.content, confirm: true }, { timeout: 120000 })).data
        .data as AttendancePreview & PollPreview & QaPreview,
    { onSuccess: onDone },
  );

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(null);
    setFileError(null);
    preview.reset();
    confirm.reset();
    if (!f) return;
    if (SPREADSHEET.test(f.name)) {
      setFileError(
        'This is an Excel file. Open it in Excel, choose File → Save As → CSV UTF-8 (.csv), then upload the .csv.',
      );
      return;
    }
    const content = await f.text();
    setFile({ name: f.name, content });
    preview.mutate({ name: f.name, content });
  };

  const result = confirm.data ?? preview.data;
  const done = Boolean(confirm.data);
  const title =
    kind === 'attendance' ? 'Upload attendee report' : kind === 'polls' ? 'Upload poll report' : 'Upload Q&A report';

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        done ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button disabled={!preview.data || confirm.isLoading} onClick={() => confirm.mutate()}>
              {confirm.isLoading ? 'Importing…' : 'Import'}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </>
        )
      }
    >
      <p className="text-table font-semibold">{webinar.title}</p>
      <p className="text-small text-ink-2">
        {day(webinar.startsAt)}
        {webinar.hasTime ? `, ${time(webinar.startsAt)}` : ''}
      </p>
      <p className="text-small mt-3">
        {kind === 'attendance'
          ? 'In Zoom: Reports → Webinars → Attendee report, export as CSV.'
          : kind === 'polls'
            ? 'In Zoom: Reports → Webinars → Poll report, export as CSV. Keep Zoom’s file name; it holds the webinar id.'
            : 'In Zoom: Reports → Webinars → Q&A report, export as CSV. Questions are kept for operators only; askers with no member record keep only their email.'}
      </p>
      {!done ? <input type="file" accept=".csv,text/csv" onChange={pick} className="mt-3 text-small" /> : null}
      {fileError ? (
        <div className="mt-3">
          <ErrorNotice>{fileError}</ErrorNotice>
        </div>
      ) : null}
      {preview.isLoading ? <LoadingPanel label="Reading the report…" height={100} /> : null}
      {preview.isError ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(preview.error, 'Could not read the report.')}</ErrorNotice>
        </div>
      ) : null}
      {confirm.isError ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(confirm.error, 'The import did not go through.')}</ErrorNotice>
        </div>
      ) : null}

      {result ? (
        <div className="mt-4 border-t border-line pt-4">
          <div className="text-small text-ink-2 mb-3">
            {done ? 'Imported' : 'Preview, nothing saved yet'} · Zoom webinar {result.webinarId}
            {result.topic ? ` · ${result.topic}` : ''}
          </div>
          {kind === 'attendance' ? (
            <>
              <div className="grid grid-cols-4 gap-4">
                <Stat label="attendees in the report" value={result.parsed} />
                <Stat label="are members" value={result.matched} />
                <Stat label="no member with that email" value={result.unmatched} />
                <Stat
                  label={done ? 'new attendance rows' : 'already imported'}
                  value={done ? result.created : result.existing}
                />
              </div>
              {result.unmatched ? (
                <details className="mt-3 text-small">
                  <summary className="cursor-pointer text-ink-2">
                    {fmt(result.unmatched)} email{result.unmatched === 1 ? '' : 's'} with no member
                    {done
                      ? ' — now listed under Unmatched attendees below'
                      : ' — they go to Unmatched attendees for review'}
                  </summary>
                  <div className="mt-2 max-h-[160px] overflow-auto">{result.unmatchedEmails.join(', ')}</div>
                </details>
              ) : null}
            </>
          ) : kind === 'qa' ? (
            <div className="grid grid-cols-4 gap-4">
              <Stat label="questions" value={result.questions} />
              <Stat label="answered" value={result.answered} />
              <Stat label="from members" value={result.fromMembers} />
              <Stat
                label={done ? 'new questions' : 'not members / anonymous'}
                value={done ? result.created : `${fmt(result.notMembers)} / ${fmt(result.anonymous)}`}
              />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-4">
                <Stat label="responses" value={result.responses} />
                <Stat label="from members" value={result.members} />
                <Stat label="polls in the file" value={result.polls.length} />
                <Stat label={done ? 'new responses' : 'report date'} value={done ? result.created : result.date} />
              </div>
              <ul className="mt-3 text-small text-ink-2">
                {result.polls.map((p, i) => (
                  <li key={i}>
                    {p.name ?? 'Poll'}: {fmt(p.responses)} responses
                  </li>
                ))}
              </ul>
            </>
          )}
          <Warnings items={result.warnings} />
        </div>
      ) : null}
    </Modal>
  );
}

function StatusPill({ w }: { w: Webinar }) {
  if (w.upcoming) return <Pill tone="planned">Upcoming</Pill>;
  if (w.attendance || w.unmatchedAttendees) return <Pill tone="healthy">Imported</Pill>;
  if (w.missingAttendance) return <Pill tone="failed">Reports missing</Pill>;
  return <Pill tone="inactive">No reports</Pill>;
}

function UnmatchedPanel({ mutable }: { mutable: boolean }) {
  const queryClient = useQueryClient();
  const [linking, setLinking] = useState<string | null>(null);
  const [memberEmail, setMemberEmail] = useState('');
  const list = useQuery<{ attendees: UnmatchedAttendee[]; total: number }>(
    'unmatched-attendees',
    async () => (await api.get('/attendance/unmatched', { params: { status: 'new' } })).data.data,
  );
  const resolve = useMutation(
    async (body: UnmatchedResolveRequest) => (await api.post('/attendance/unmatched/resolve', body)).data.data,
    {
      onSuccess: () => {
        setLinking(null);
        setMemberEmail('');
        queryClient.invalidateQueries('unmatched-attendees');
        queryClient.invalidateQueries('webinars');
      },
    },
  );
  const attendees = list.data?.attendees ?? [];

  return (
    <Panel className="mt-6">
      <PanelHeader title="Unmatched attendees" note={list.data ? `${fmt(list.data.total)} to review` : undefined} />
      {list.isLoading ? <LoadingPanel label="Loading…" height={120} /> : null}
      {list.isError ? <ErrorNotice>Could not load unmatched attendees.</ErrorNotice> : null}
      {resolve.isError ? (
        <ErrorNotice>{errorMessage(resolve.error, 'Could not save the decision.')}</ErrorNotice>
      ) : null}
      {list.data && attendees.length === 0 ? (
        <EmptyState title="Nothing to review">
          Every attendee email in an uploaded report belongs to a member or has been decided.
        </EmptyState>
      ) : null}
      {attendees.length ? (
        <table className="w-full text-small">
          <thead>
            <tr className="text-left text-ink-2">
              {['Email', 'Name', 'Webinars', ''].map((h) => (
                <th key={h} className="px-5 py-2 font-normal">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {attendees.map((a) => (
              <tr key={a.email} className="border-t border-line align-top">
                <td className="px-5 py-2">{a.email}</td>
                <td className="px-5 py-2">{a.name || '—'}</td>
                <td className="px-5 py-2 text-ink-2">
                  {a.sessions
                    .slice(0, 2)
                    .map((s) => `${day(s.occurredAt)}${s.sessionTopic ? ` · ${s.sessionTopic}` : ''}`)
                    .join('; ')}
                  {a.sessions.length > 2 ? ` +${a.sessions.length - 2}` : ''}
                </td>
                <td className="px-5 py-2 whitespace-nowrap text-right">
                  {linking === a.email ? (
                    <span className="inline-flex items-center gap-2">
                      <input
                        type="email"
                        placeholder="member’s email"
                        value={memberEmail}
                        onChange={(e) => setMemberEmail(e.target.value)}
                        className="border border-line-strong px-2 py-[5px] text-small w-[200px]"
                      />
                      <Button
                        size="sm"
                        disabled={!memberEmail || resolve.isLoading}
                        onClick={() => resolve.mutate({ email: a.email, action: 'link', memberEmail })}
                      >
                        Link
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setLinking(null)}>
                        Cancel
                      </Button>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!mutable}
                        onClick={() => {
                          setLinking(a.email);
                          setMemberEmail('');
                        }}
                      >
                        Link to member
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!mutable || resolve.isLoading}
                        onClick={() => resolve.mutate({ email: a.email, action: 'guest' })}
                      >
                        Guest
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!mutable || resolve.isLoading}
                        onClick={() => resolve.mutate({ email: a.email, action: 'ignore' })}
                      >
                        Ignore
                      </Button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Panel>
  );
}

function SummaryStats({ s }: { s: Summary }) {
  const dash = (v: number | null) => (v === null ? '—' : v);
  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        <Stat label="live attendees" value={s.liveAttendees} />
        <Stat label="unique viewers (Zoom)" value={dash(s.uniqueViewers)} />
        <Stat label="peak concurrent" value={dash(s.peakConcurrent)} />
        <Stat label="poll responses" value={s.pollResponses + s.surveyResponses} />
        <Stat label="questions / answered" value={`${fmt(s.questions)} / ${fmt(s.answered)}`} />
        <Stat label="recording viewers (Moodle)" value={s.recordingViewers} />
      </div>
      {s.attendees || s.nonMembers ? (
        <p className="text-small text-ink-2 mt-2">
          {fmt(s.attendees)} member{s.attendees === 1 ? '' : 's'} and {fmt(s.nonMembers)} not matched to a member
          {s.questions
            ? ` · ${fmt(s.questionsFromMembers)} question${s.questionsFromMembers === 1 ? '' : 's'} from members`
            : ''}
        </p>
      ) : null}
      {s.polls?.length ? (
        <div className="mt-4 grid grid-cols-2 gap-4">
          {s.polls.map((p, i) => (
            <div key={i} className="text-small">
              <div className="font-semibold">{p.question}</div>
              <ul className="mt-1 space-y-[2px]">
                {p.answers.map((a) => (
                  <li key={a.value} className="flex justify-between gap-3 border-b border-line">
                    <span>{a.value}</span>
                    <span className="tabular text-ink-2">
                      {fmt(a.n)}
                      {p.total ? ` (${Math.round((a.n / p.total) * 100)}%)` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

function ProductionForm({
  webinar,
  production,
  onDone,
  onCancel,
}: {
  webinar: WebinarDetail;
  production: Production;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [speakerIds, setSpeakerIds] = useState<string[]>(production.speakers.map((p) => p.id));
  const [moderatorId, setModeratorId] = useState(production.moderator?.id ?? '');
  const [coordinator, setCoordinator] = useState(production.coordinator ?? '');
  const [abstract, setAbstract] = useState(production.abstract ?? '');
  const [zoomWebinarId, setZoomWebinarId] = useState(webinar.webinarId ?? '');
  const [bios, setBios] = useState<Record<string, string>>(
    Object.fromEntries(
      [...production.speakers, ...(production.moderator ? [production.moderator] : [])].map((p) => [p.id, p.bio ?? '']),
    ),
  );
  const contacts = useQuery<Contact[]>(
    'contacts-all',
    async () =>
      (await api.get<DataResponse<ContactPage>>('/contacts', { params: { limit: 200 } satisfies ContactListQuery }))
        .data.data.contacts,
  );
  const save = useMutation(
    async () =>
      (
        await api.put(`/webinars/${webinar.id}/production`, {
          speakerIds,
          moderatorId: moderatorId || null,
          coordinator,
          abstract,
          bios,
          zoomWebinarId: zoomWebinarId || undefined,
        } satisfies WebinarProductionRequest)
      ).data.data,
    { onSuccess: onDone },
  );
  const all = contacts.data ?? [];
  const byId = new Map(all.map((c) => [c.id, c]));
  const nameOf = (id: string) =>
    byId.get(id)?.name ?? production.speakers.find((p) => p.id === id)?.name ?? production.moderator?.name ?? 'Contact';
  const speakerChoices = [...all].sort(
    (a, b) => Number(b.kinds.includes('speaker')) - Number(a.kinds.includes('speaker')),
  );
  const field = 'border border-line px-2 py-[6px] text-small w-full';

  return (
    <div className="space-y-3 text-small">
      {contacts.isError ? <ErrorNotice>Could not load contacts.</ErrorNotice> : null}
      <div>
        <div className="text-ink-2 mb-1">Speakers (contacts; add new people on the Contacts page first)</div>
        {speakerIds.map((id) => (
          <div key={id} className="border border-line p-2 mb-2">
            <div className="flex items-center justify-between">
              <span className="font-semibold">{nameOf(id)}</span>
              <Button size="sm" variant="ghost" onClick={() => setSpeakerIds(speakerIds.filter((x) => x !== id))}>
                Remove
              </Button>
            </div>
            <textarea
              className={`${field} mt-1`}
              rows={2}
              placeholder="Bio for the roll-out"
              value={bios[id] ?? ''}
              onChange={(e) => setBios({ ...bios, [id]: e.target.value })}
            />
          </div>
        ))}
        <select
          className={field}
          value=""
          onChange={(e) => e.target.value && setSpeakerIds([...speakerIds, e.target.value])}
        >
          <option value="">Add a speaker…</option>
          {speakerChoices
            .filter((c) => !speakerIds.includes(c.id))
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.kinds.includes('speaker') ? '' : ' (not tagged speaker)'}
              </option>
            ))}
        </select>
      </div>
      <label className="block">
        <span className="text-ink-2">Moderator</span>
        <select className={field} value={moderatorId} onChange={(e) => setModeratorId(e.target.value)}>
          <option value="">—</option>
          {all.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      {moderatorId ? (
        <textarea
          className={field}
          rows={2}
          placeholder="Moderator title or bio"
          value={bios[moderatorId] ?? ''}
          onChange={(e) => setBios({ ...bios, [moderatorId]: e.target.value })}
        />
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-ink-2">Technical coordinator</span>
          <input className={field} value={coordinator} onChange={(e) => setCoordinator(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-ink-2">Zoom webinar ID</span>
          <input
            className={field}
            value={zoomWebinarId}
            onChange={(e) => setZoomWebinarId(e.target.value)}
            placeholder="e.g. 913 8960 6122"
          />
        </label>
      </div>
      <label className="block">
        <span className="text-ink-2">Abstract</span>
        <textarea className={field} rows={3} value={abstract} onChange={(e) => setAbstract(e.target.value)} />
      </label>
      {save.isError ? <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice> : null}
      <div className="flex gap-2">
        <Button size="sm" disabled={save.isLoading} onClick={() => save.mutate()}>
          {save.isLoading ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function WebinarDetailPanel({ id, mutable, onChange }: { id: string; mutable: boolean; onChange: () => void }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const detail = useQuery<WebinarDetail>(['webinar', id], async () => (await api.get(`/webinars/${id}`)).data.data);
  const doc = useMutation(
    async (kind: 'speaker-info' | 'rollout') =>
      (await api.get(`/webinars/${id}/documents/${kind}`)).data.data as { html: string },
  );
  const openDoc = (kind: 'speaker-info' | 'rollout') => {
    const tab = openBlankTab();
    doc.mutate(kind, { onSuccess: (d) => showPrintable(tab, d.html), onError: () => tab?.close() });
  };
  const refresh = () => {
    queryClient.invalidateQueries(['webinar', id]);
    onChange();
  };

  if (detail.isLoading) return <LoadingPanel label="Loading…" height={120} />;
  if (detail.isError || !detail.data) return <ErrorNotice>Could not load this webinar.</ErrorNotice>;
  const w = detail.data;
  const p = w.production;
  const describe = (x: Person) =>
    [x.name, [x.title, x.organization].filter(Boolean).join(', ')].filter(Boolean).join(' — ');

  return (
    <div className="grid gap-8 py-2" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.3fr)' }}>
      <div className="space-y-6">
        <section>
          <div className="font-semibold text-table mb-3">Session summary</div>
          <SummaryStats s={w.summary} />
        </section>
        <section>
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-table">Speakers and documents</div>
            {mutable && !editing ? (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : null}
          </div>
          {editing ? (
            <ProductionForm
              webinar={w}
              production={p}
              onDone={() => {
                setEditing(false);
                refresh();
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <div className="text-small space-y-1">
              <div>
                <span className="text-ink-2">Speakers:</span>{' '}
                {p.speakers.length ? p.speakers.map(describe).join('; ') : '—'}
              </div>
              <div>
                <span className="text-ink-2">Moderator:</span> {p.moderator ? describe(p.moderator) : '—'}
              </div>
              <div>
                <span className="text-ink-2">Technical coordinator:</span> {p.coordinator ?? '—'}
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              variant="outline"
              disabled={doc.isLoading || !w.hasTime}
              onClick={() => openDoc('speaker-info')}
            >
              Info for speaker
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={doc.isLoading || !w.hasTime}
              onClick={() => openDoc('rollout')}
            >
              Moderator roll-out
            </Button>
          </div>
          <p className="text-small text-ink-2 mt-2">
            {w.hasTime
              ? 'Prepared from this webinar’s details; opens in a new tab to edit, copy or print. Nothing is sent.'
              : 'Documents need the start time from the website event.'}
          </p>
          {doc.isError ? (
            <div className="mt-2">
              <ErrorNotice>{errorMessage(doc.error, 'Could not prepare the document.')}</ErrorNotice>
            </div>
          ) : null}
        </section>
        {w.questionList ? (
          <section>
            <div className="font-semibold text-table mb-2">Q&amp;A ({fmt(w.questionList.length)})</div>
            {w.questionList.length ? (
              <details className="text-small">
                <summary className="cursor-pointer text-ink-2">
                  Show questions (personal data: operators only, never sent to the knowledge base)
                </summary>
                <ol className="mt-2 space-y-2">
                  {w.questionList.map((q) => (
                    <li key={q.position} className="border-b border-line pb-2">
                      <div className="whitespace-pre-line">{q.question}</div>
                      <div className="text-small text-ink-2">
                        {q.member ? q.member.name : 'Not a member or anonymous'} ·{' '}
                        {q.answered ? (q.answerMode === 'live' ? 'answered live' : 'answered') : 'not answered'}
                        {q.upvotes ? ` · ${q.upvotes} upvote${q.upvotes === 1 ? '' : 's'}` : ''}
                      </div>
                    </li>
                  ))}
                </ol>
              </details>
            ) : (
              <p className="text-small text-ink-2">No Q&amp;A report uploaded.</p>
            )}
          </section>
        ) : null}
      </div>
      <section>
        <div className="font-semibold text-table mb-3">Production checklist</div>
        {w.isWebinar ? (
          <Checklist subjectType="event" subjectId={w.id} mutable={mutable} onChange={refresh} />
        ) : (
          <EmptyState title="No checklist">Only online events with a date get a production checklist.</EmptyState>
        )}
      </section>
    </div>
  );
}

export default function Webinars() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('past');
  const [upload, setUpload] = useState<{ webinar: Webinar; kind: Kind } | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const list = useQuery<Webinar[]>('webinars', async () => (await api.get('/webinars')).data.data);
  const refresh = () => {
    queryClient.invalidateQueries('webinars');
    queryClient.invalidateQueries('webinar');
    queryClient.invalidateQueries('checklist');
    queryClient.invalidateQueries('unmatched-attendees');
    queryClient.invalidateQueries('dashboard');
  };

  const rows = (list.data ?? []).filter((w) =>
    filter === 'all' ? true : filter === 'upcoming' ? w.upcoming : !w.upcoming,
  );
  const ordered = filter === 'upcoming' ? [...rows].reverse() : rows;
  const missing = (list.data ?? []).filter((w) => w.missingAttendance).length;

  return (
    <>
      <PageHeader
        title="Webinars"
        sub="Online events from the website, each with its production checklist. After each webinar, upload Zoom’s attendee, poll and Q&A reports here. Late steps, such as an attendee report still missing a day after the webinar, go to the Program Manager’s daily checklist email."
      />

      {missing ? (
        <div className="mb-5">
          <ErrorNotice>
            {fmt(missing)} webinar{missing === 1 ? '' : 's'} from the last 60 days {missing === 1 ? 'has' : 'have'} no
            attendance yet.
          </ErrorNotice>
        </div>
      ) : null}

      <div className="flex items-center gap-2 mb-5">
        {(['past', 'upcoming', 'all'] as Filter[]).map((f) => (
          <Button key={f} size="sm" variant={f === filter ? 'dark' : 'ghost'} onClick={() => setFilter(f)}>
            {f === 'past' ? 'Past' : f === 'upcoming' ? 'Upcoming' : 'All'}
          </Button>
        ))}
      </div>

      {!mutable ? (
        <div className="mb-5">
          <InfoNotice>Viewers can see what has been imported; uploading needs an admin login.</InfoNotice>
        </div>
      ) : null}

      <Panel>
        {list.isLoading ? <LoadingPanel label="Loading webinars…" /> : null}
        {list.isError ? <ErrorNotice>Could not load webinars.</ErrorNotice> : null}
        {list.data && ordered.length === 0 ? (
          <EmptyState title="No webinars">Online events appear here once they are published on the website.</EmptyState>
        ) : null}
        {ordered.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-small">
              <thead>
                <tr className="text-left text-ink-2">
                  {['Date', 'Webinar', 'Attendance', 'Poll responses', 'Checklist', 'Status', 'Upload Zoom CSV'].map(
                    (h) => (
                      <th key={h} className="px-5 py-2 font-normal">
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {ordered.map((w) => (
                  <Fragment key={w.id}>
                    <tr className="border-t border-line align-top">
                      <td className="px-5 py-3 whitespace-nowrap tabular">
                        {day(w.startsAt)}
                        {w.hasTime ? (
                          <div className="text-ink-2">
                            {time(w.startsAt)}
                            {w.endsAt ? `–${time(w.endsAt)}` : ''}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-5 py-3 min-w-[260px]">
                        {w.url ? (
                          <a href={w.url} target="_blank" rel="noreferrer" className="link-underline">
                            {w.title}
                          </a>
                        ) : (
                          w.title
                        )}
                        {w.webinarId ? <div className="text-small text-ink-2">Zoom {w.webinarId}</div> : null}
                        {w.questions ? (
                          <div className="text-small text-ink-2">
                            {fmt(w.questions)} Q&amp;A question{w.questions === 1 ? '' : 's'}
                          </div>
                        ) : null}
                        <button
                          className="block font-semibold text-small text-ink-2 hover:text-ink mt-1"
                          onClick={() => setOpen(open === w.id ? null : w.id)}
                        >
                          {open === w.id ? 'Close ▴' : 'Checklist, summary, documents ▾'}
                        </button>
                      </td>
                      <td className="px-5 py-3 tabular">
                        {fmt(w.attendance)}
                        {w.unmatchedAttendees ? (
                          <div className="text-small text-ink-2">+{fmt(w.unmatchedAttendees)} not members</div>
                        ) : null}
                      </td>
                      <td className="px-5 py-3 tabular">
                        {fmt(w.pollResponses)}
                        {w.surveyResponses ? (
                          <div className="text-small text-ink-2">+{fmt(w.surveyResponses)} website survey</div>
                        ) : null}
                      </td>
                      <td className="px-5 py-3 w-[160px]">
                        {w.checklist ? (
                          <ProgressBar
                            finished={w.checklist.finished}
                            total={w.checklist.total}
                            overdue={w.checklist.overdue}
                          />
                        ) : (
                          <span className="text-ink-2">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <StatusPill w={w} />
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-right">
                        <span className="inline-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!mutable || w.upcoming}
                            onClick={() => setUpload({ webinar: w, kind: 'attendance' })}
                          >
                            Attendees
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!mutable || w.upcoming}
                            onClick={() => setUpload({ webinar: w, kind: 'polls' })}
                          >
                            Poll
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!mutable || w.upcoming}
                            onClick={() => setUpload({ webinar: w, kind: 'qa' })}
                          >
                            Q&amp;A
                          </Button>
                        </span>
                      </td>
                    </tr>
                    {open === w.id ? (
                      <tr className="bg-paper">
                        <td colSpan={7} className="px-5 py-4">
                          <WebinarDetailPanel id={w.id} mutable={mutable} onChange={refresh} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>

      <UnmatchedPanel mutable={mutable} />

      {upload ? (
        <UploadModal webinar={upload.webinar} kind={upload.kind} onClose={() => setUpload(null)} onDone={refresh} />
      ) : null}
    </>
  );
}
